import type { ReactNode } from 'react'
import { Sparkline } from './charts/Sparkline.js'
import { formatSignedDelta } from '../lib/format.js'

export type MetricTone = 'default' | 'pos' | 'warn' | 'neg'

export interface MetricProps {
  readonly label: string
  readonly value: string | number
  readonly unit?: string
  readonly delta?: number
  readonly deltaSuffix?: string
  readonly tone?: MetricTone
  readonly trend?: readonly (number | null)[] | null
  readonly footnote?: ReactNode
  /**
   * Denser tile for metrics shown INSIDE a panel rather than across the page header.
   * The panel row is `minmax(320px, 1fr)`, so a full-size tile there has about 43px of
   * content once padding is taken off — which is what clipped "SUBMITTE(D)" and pushed
   * 103.2h outside its border.
   */
  readonly compact?: boolean
}

export function trendDelta<T>(
  series: readonly T[] | null | undefined,
  readValue: (point: T) => number | null | undefined,
  scale = 1,
): number | undefined {
  if (series === null || series === undefined || series.length < 2) return undefined

  let latest: number | undefined
  let latestIndex = -1
  let previous: number | undefined

  for (let index = series.length - 1; index >= 0; index -= 1) {
    const value = readValue(series[index]!)
    if (value !== null && value !== undefined) {
      latest = value
      latestIndex = index
      break
    }
  }

  if (latest === undefined) return undefined

  // Strictly BELOW the latest point: starting at length - 2 double-counted the latest
  // bucket whenever trailing buckets were null, turning "no comparison" into a 0 delta.
  for (let index = latestIndex - 1; index >= 0; index -= 1) {
    const value = readValue(series[index]!)
    if (value !== null && value !== undefined) {
      previous = value
      break
    }
  }

  if (previous === undefined) return undefined

  return Math.round((latest - previous) * scale)
}

function toneColor(tone: MetricTone): string {
  if (tone === 'pos') return 'var(--positive)'
  if (tone === 'warn') return 'var(--warning)'
  if (tone === 'neg') return 'var(--negative)'
  return 'var(--brand)'
}

export function Metric({
  label,
  value,
  unit,
  delta,
  deltaSuffix,
  tone = 'default',
  trend,
  footnote,
  compact = false,
}: MetricProps) {
  const hasTrend = trend !== null && trend !== undefined && trend.some((point) => point !== null)
  const showTrend = hasTrend && delta !== undefined
  const deltaTone =
    delta === undefined
      ? 'var(--fg-muted)'
      : delta > 0
        ? 'var(--positive)'
        : delta < 0
          ? 'var(--negative)'
          : 'var(--fg-muted)'
  const seriesColor = toneColor(tone)

  return (
    <div
      data-metric={label}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        padding: compact ? 'var(--o-space-3)' : 'var(--o-space-4)',
        display: 'grid',
        gap: compact ? 'var(--o-space-1)' : 'var(--o-space-2)',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--o-space-2)' }}>
        <div className="card-label">{label}</div>
        <span style={{ color: deltaTone, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          {formatSignedDelta(delta, deltaSuffix ?? '')}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--o-space-1)' }}>
        <span style={{ fontSize: compact ? 21 : 30, fontWeight: 500, fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
          {value}
        </span>
        {unit !== undefined ? <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{unit}</span> : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
        <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{footnote}</span>
        {showTrend ? <Sparkline data={trend!} width={78} height={22} stroke={seriesColor} fill={seriesColor} /> : null}
      </div>
    </div>
  )
}
